import { Box, Button, Card, FormControlLabel, Grid, IconButton, MenuItem, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Theme, Typography, createStyles, makeStyles } from '@material-ui/core';
import { ArrowBack, Edit, Save } from '@material-ui/icons';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CurrencyInput } from '../../components/CurrencyInput';
import Logger from '../../components/Logger';
import Page from '../../containers/Page/Page';
import { useAuth } from '../../hooks/auth';
import { api } from '../../services/api';
import { ILog } from '../../types/Logger';
import { Helpers } from '../../utils/Helpers';

interface ITabela {
    codigo_sap: string;
    label: string;
    preco_lista: string;
    mercado: string;
    fator_revenda: string;
    upk: string;
    data_preco: string;
    fator_custo: string;
    relacionados: {
        nome: string;
        url: string;
    }[];
}

interface IParams {
    id: string;
}

function TabelasCreate() {

    const history = useHistory();
    const classes = useStyles();
    const params = useParams<IParams>();
    const { data } = useAuth();

    const { enqueueSnackbar: alert } = useSnackbar();

    const [error, setError] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);
    const [tabela, setTabela] = React.useState<ITabela>({} as ITabela);
    const [ativa, setAtiva] = React.useState<boolean>(true);
    const [logs, setLogs] = React.useState<ILog[]>([]);

    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        setTabela({
            ...tabela,
            data_preco: moment().format('YYYY-MM-DD'),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleFetch() {
        if (params.id) {
            setLoading(true);
            try {
                const response = await api.get(`tabelas/${params.id}`);
                setTabela(response.data.object);
                setAtiva(response.data.object.ativa);
                setLogs(response.data.object.logs);
                setError('');
            } catch (error: any) {
                setError(Helpers.errorFormat(error));
            }
            setLoading(false);
        }
    }

    async function handleSave() {
        setLoading(true);
        try {
            const response = await api.request({
                url: params.id ? `tabelas/${params.id}` : 'tabelas',
                method: params.id ? 'PUT' : 'POST',
                data: { ...tabela, ativa },
            });
            alert(response.data.message, { variant: 'success' });
            if (!params.id) { history.push(`/tabelas/${response.data.id}`); }
        } catch (error) {
            Helpers.error(error, alert);
        }
        setLoading(false);
    }

    return (
        <Page error={error} loading={loading}>
            <Page.Title>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <IconButton size="small" color="primary" className={classes.backButton} onClick={() => history.goBack()}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        {params.id ? 'Editar Tabela' : 'Nova Tabela'}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    {params.id &&
                        <Box mr={2}>
                            <Logger
                                logs={logs}
                            />
                        </Box>
                    }
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => handleSave()}>
                        Salvar
                    </Button>
                </Box>
            </Page.Title>
            <Page.Section>
                <Page.Content>
                    <Box p={2}>
                        {params.id &&
                            <Box mb={2} display="flex" justifyContent="end">
                                <FormControlLabel
                                    control={<Switch checked={Boolean(ativa)} onChange={(event) => setAtiva(event.target.checked)} name="checkedA" />}
                                    label="Ativo"
                                />
                            </Box>
                        }
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="codigo_sap"
                                    name="codigo_sap"
                                    label="Codigo SAP"
                                    variant="outlined"
                                    fullWidth={true}
                                    value={tabela.codigo_sap || ''}
                                    onChange={(event) => setTabela({ ...tabela, codigo_sap: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="label"
                                    name="label"
                                    label="Label"
                                    variant="outlined"
                                    fullWidth={true}
                                    value={tabela.label || ''}
                                    onChange={(event) => setTabela({ ...tabela, label: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CurrencyInput
                                    hideSymbol={true}
                                    value={Number.parseFloat(tabela.preco_lista) || 0}
                                    onChangeValue={(_e, ov) => setTabela({ ...tabela, preco_lista: ov.toString() })}
                                    InputElement={
                                        <TextField
                                            id="preco_lista"
                                            name="preco_lista"
                                            label="Preço de Lista"
                                            variant="outlined"
                                            fullWidth={true}
                                        />
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="mercado"
                                    name="mercado"
                                    label="Mercado"
                                    variant="outlined"
                                    fullWidth={true}
                                    select={true}
                                    value={tabela.mercado || ''}
                                    onChange={(event) => setTabela({ ...tabela, mercado: event.target.value })}
                                >
                                    <MenuItem value="MI">Mercado Interno</MenuItem>
                                    <MenuItem value="ME">Mercado Externo</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="data_preco"
                                    name="data_preco"
                                    label="Data de Preço"
                                    variant="outlined"
                                    type="date"
                                    fullWidth={true}
                                    value={tabela.data_preco || ''}
                                    onChange={(event) => setTabela({ ...tabela, data_preco: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="upk"
                                    name="upk"
                                    label="UPK"
                                    variant="outlined"
                                    fullWidth={true}
                                    value={tabela.upk || ''}
                                    onChange={(event) => setTabela({ ...tabela, upk: event.target.value })}
                                />
                            </Grid>
                            {(data.user.papel === 4 || data.user.papel === 3 || data.user.papel === 5) &&
                                <React.Fragment>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            id="fator_revenda"
                                            name="fator_revenda"
                                            label="Fator de Revenda"
                                            variant="outlined"
                                            fullWidth={true}
                                            value={tabela.fator_revenda ?? ''}
                                            onChange={(event) => setTabela({ ...tabela, fator_revenda: event.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            id="fator_custo"
                                            name="fator_custo"
                                            label="Fator de Custo"
                                            variant="outlined"
                                            fullWidth={true}
                                            value={tabela.fator_custo ?? ''}
                                            onChange={(event) => setTabela({ ...tabela, fator_custo: event.target.value })}
                                        />
                                    </Grid>
                                </React.Fragment>
                            }
                        </Grid>
                        {tabela.relacionados &&
                            <React.Fragment>
                                <Box my={2}>
                                    <Typography variant="h6" component="div">
                                        Relacionados
                                    </Typography>
                                </Box>
                                <Card variant="outlined">
                                    <TableContainer>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Nome</TableCell>
                                                    <TableCell align="center">Editar</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {tabela.relacionados.map((row, index) => (
                                                    <TableRow key={index} hover={true}>
                                                        <TableCell>{row.nome}</TableCell>
                                                        <TableCell align="center">
                                                            <IconButton size="small" color="primary" onClick={() => history.push(row.url)}>
                                                                <Edit />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Card>
                            </React.Fragment>
                        }
                    </Box>
                </Page.Content>
            </Page.Section>
        </Page>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButton: {
            marginRight: theme.spacing(1),
        },
        row: {
            marginBottom: theme.spacing(1)
        },
        custom: {
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1,
        },
    })
);

export default TabelasCreate;